import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Building2, FileText, MapPin, ContactRound, Users2, Clock, Eye, CheckCircle2, Pencil, Trash2 } from "lucide-react";
import EmptyStateFlua from "@/components/EmptyStateFlua";
import { SkeletonFluaRow } from "@/components/SkeletonFlua";
import { fmtInt, fmtPct, pct } from "@/lib/format";
import { STATUS_LABEL } from "./constants";

export default function CondominiosTable({ loading, filtered, isProsp, metricsForCondo, openPanel, handleConverterEmAtendido, openEdit, handleDelete }) {
  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="thin-scroll overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Condomínio</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead className="text-right">Apt/Salas</TableHead>
              {!isProsp && <TableHead className="text-right">Clientes</TableHead>}
              <TableHead>Responsável</TableHead>
              <TableHead>Consultor</TableHead>
              <TableHead>Status</TableHead>
              {isProsp ? (
                <TableHead>Aprovação prevista</TableHead>
              ) : (
                <TableHead>Semana</TableHead>
              )}
              {!isProsp && (
                <>
                  <TableHead className="text-right">Meta</TableHead>
                  <TableHead className="text-right">Vendas</TableHead>
                  <TableHead className="min-w-[180px]">% Atingido</TableHead>
                  <TableHead className="min-w-[220px]">Clientes · Total / Mês</TableHead>
                </>
              )}
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <>
                {Array.from({ length: 5 }).map((_, k) => (
                  <TableRow key={`skel-${k}`}>
                    <TableCell colSpan={isProsp ? 8 : 13} className="p-0">
                      <SkeletonFluaRow cols={isProsp ? 8 : 13} />
                    </TableCell>
                  </TableRow>
                ))}
              </>
            )}
            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={isProsp ? 8 : 13} className="p-0">
                  <EmptyStateFlua
                    icon={Building2}
                    title={isProsp ? "Nenhum prospecto cadastrado" : "Nenhum condomínio atendido"}
                    description={isProsp
                      ? "Adicione condomínios que ainda precisam de infraestrutura pra começar a mapear oportunidades."
                      : "Cadastre condomínios atendidos pra acompanhar metas e clientes por prédio."}
                    className="border-0"
                  />
                </TableCell>
              </TableRow>
            )}
            {filtered.map((i) => {
              const p = pct(i.vendas_realizadas, i.meta_vendas);
              const tone = p >= 100 ? "text-emerald-500" : p >= 50 ? "text-amber-500" : "text-red-500";
              const m = metricsForCondo(i);
              const pctTotal = m.metaTotal > 0 ? Math.min(100, (m.realizadoTotal * 100) / m.metaTotal) : 0;
              const pctMes = m.metaMensal > 0 ? Math.min(100, (m.realizadoMes * 100) / m.metaMensal) : 0;
              const toneT = pctTotal >= 100 ? "text-emerald-500" : pctTotal >= 50 ? "text-amber-500" : "text-red-500";
              const toneM = pctMes >= 100 ? "text-emerald-500" : pctMes >= 50 ? "text-amber-500" : "text-red-500";
              return (
                <TableRow key={i.id} data-testid={`condominio-row-${i.id}`}>
                  <TableCell className="font-medium">
                    <div>{i.condominio}</div>
                    {(i.cnpj || i.endereco) && (
                      <div className="text-[11px] text-muted-foreground mt-0.5 space-y-0.5">
                        {i.cnpj && (
                          <div className="flex items-center gap-1 tabular-nums font-mono" title="CNPJ">
                            <FileText className="w-3 h-3" /> {i.cnpj}
                          </div>
                        )}
                        {i.endereco && (
                          <div className="flex items-center gap-1 truncate max-w-[280px]" title={i.endereco + (i.complemento ? ` · ${i.complemento}` : "")}>
                            <MapPin className="w-3 h-3 shrink-0" />
                            <span className="truncate">{i.endereco}</span>
                          </div>
                        )}
                        {Array.isArray(i.contatos) && i.contatos.length > 0 && (
                          <div className="flex items-center gap-1" title={i.contatos.map((c) => c.nome).filter(Boolean).join(", ")}>
                            <ContactRound className="w-3 h-3" />
                            <span>{i.contatos.length} contato{i.contatos.length > 1 ? "s" : ""}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={i.tipo === "comercial" ? "secondary" : "outline"} className="capitalize">{i.tipo}</Badge>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{fmtInt(i.qtd_apartamentos)}</TableCell>
                  {!isProsp && <TableCell className="text-right tabular-nums">{fmtInt(i.qtd_clientes_atuais)}</TableCell>}
                  <TableCell>{i.responsavel || "—"}</TableCell>
                  <TableCell>
                    {i.consultor ? (
                      <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30" data-testid={`consultor-badge-${i.id}`}>
                        {i.consultor}
                      </Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{STATUS_LABEL[i.status_contato] || i.status_contato}</Badge>
                  </TableCell>
                  {isProsp ? (
                    <TableCell className="text-sm text-muted-foreground">
                      {i.data_aprovacao_prevista || "—"}
                    </TableCell>
                  ) : (
                    <TableCell className="text-sm text-muted-foreground">{i.semana_planejada || "—"}</TableCell>
                  )}
                  {!isProsp && (
                    <>
                      <TableCell className="text-right tabular-nums">{fmtInt(i.meta_vendas)}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmtInt(i.vendas_realizadas)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Progress value={Math.min(p, 100)} className="h-2 flex-1" />
                          <span className={`text-xs font-semibold tabular-nums w-14 text-right ${tone}`}>{fmtPct(p)}</span>
                        </div>
                      </TableCell>
                      <TableCell data-testid={`condo-metas-clientes-${i.id}`}>
                        {(m.metaTotal > 0 || m.metaMensal > 0) ? (
                          <div className="space-y-1.5">
                            {/* Meta geral (total do prédio) */}
                            <div className="flex items-center gap-2">
                              <Users2 className="w-3 h-3 text-muted-foreground shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between text-[10px]">
                                  <span className="text-muted-foreground">Total</span>
                                  <span className={`tabular-nums font-semibold ${toneT}`}>
                                    {fmtInt(m.realizadoTotal)} / {fmtInt(m.metaTotal || 0)}
                                  </span>
                                </div>
                                <Progress value={pctTotal} className="h-1.5" />
                              </div>
                            </div>
                            {/* Meta mensal */}
                            <div className="flex items-center gap-2">
                              <Clock className="w-3 h-3 text-muted-foreground shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between text-[10px]">
                                  <span className="text-muted-foreground">Mês</span>
                                  <span className={`tabular-nums font-semibold ${toneM}`}>
                                    {fmtInt(m.realizadoMes)} / {fmtInt(m.metaMensal || 0)}
                                  </span>
                                </div>
                                <Progress value={pctMes} className="h-1.5" />
                              </div>
                            </div>
                          </div>
                        ) : (
                          <span className="text-[11px] text-muted-foreground italic">Sem meta de clientes definida</span>
                        )}
                      </TableCell>
                    </>
                  )}
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => openPanel(i)}
                        data-testid={`btn-panel-condominio-${i.id}`}
                        title="Ver painel: clientes e prospectos deste prédio"
                        className="text-primary"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      {isProsp && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleConverterEmAtendido(i)}
                          data-testid={`btn-aprovar-infra-${i.id}`}
                          title="Aprovar infraestrutura"
                          className="text-emerald-500 border-emerald-500/30 hover:bg-emerald-500/10"
                        >
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Aprovar
                        </Button>
                      )}
                      <Button size="icon" variant="ghost" onClick={() => openEdit(i)} data-testid={`btn-edit-condominio-${i.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => handleDelete(i.id)} data-testid={`btn-delete-condominio-${i.id}`}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
